import { useState, useMemo } from 'react';
import { __ } from '@wordpress/i18n';
import Header from '../Global/Header';
import Button from '../Common/Button';
import SbUtils from '../../Utils/SbUtils';
import ReviewAlertsScreenContext from '../Context/ReviewAlertsScreenContext';
import ReviewAlertsList from './ReviewAlerts/ReviewAlertsList';
import ReviewAlertsEmptyState from './ReviewAlerts/ReviewAlertsEmptyState';
import ModalContainer from '../Global/ModalContainer';
import CustomizerSourcesModal from '../Modals/CustomizerSourcesModal';
import FullScreenLoader from '../Global/FullScreenLoader';

const ReviewAlertsScreen = ( {
    editorTopLoader,
    editorNotification,
    editorConfirmDialog,
    isPro,
    upsellModal,
    noticeBar
} ) => {
    const sbCustomizer = window.sb_customizer;

    // Modal state for source selection
    const [ showSourcesModal, setShowSourcesModal ] = useState( false );
    const [ isCreatingPopup, setIsCreatingPopup ] = useState( false );

    // Popups list state
    const [ popupsList, setPopupsList ] = useState(
        sbCustomizer?.reviewAlerts?.list || []
    );

    // Total popups count for pagination
    const [ popupsCount, setPopupsCount ] = useState(
        sbCustomizer?.reviewAlerts?.count || 0
    );

    // eslint-disable-next-line no-unused-vars
    const popups = useMemo(
        () => ( { popupsList, setPopupsList, popupsCount, setPopupsCount } ),
        [ popupsList, setPopupsList, popupsCount, setPopupsCount ]
    );

    // Placeholder state for Header context compatibility
    const [ addApiKeyModal, setAddApiKeyModal ] = useState( { active: false } );
    const apiKeyModal = useMemo(
        () => ( { addApiKeyModal, setAddApiKeyModal } ),
        [ addApiKeyModal, setAddApiKeyModal ]
    );

    const [ apiKeys, setApiKeys ] = useState( sbCustomizer?.apiKeys || [] );
    const apis = useMemo(
        () => ( { apiKeys, setApiKeys } ),
        [ apiKeys, setApiKeys ]
    );

    const [ apiKeyLimits, setApiKeyLimits ] = useState( sbCustomizer?.apiKeyLimits || [] );
    const apiLimits = useMemo(
        () => ( { apiKeyLimits, setApiKeyLimits } ),
        [ apiKeyLimits, setApiKeyLimits ]
    );

    const [ freeRetrieverData, setFreeRetrieverData ] = useState( sbCustomizer?.freeRetrieverData || {} );
    const freeRet = useMemo(
        () => ( { freeRetrieverData, setFreeRetrieverData } ),
        [ freeRetrieverData, setFreeRetrieverData ]
    );

    const [ modalType, setModalType ] = useState( 'addSource' );
    const addSModal = useMemo(
        () => ( { modalType, setModalType } ),
        [ modalType, setModalType ]
    );

    const [ retModalType, setRetModalType ] = useState( '' );
    const freeRetModal = useMemo(
        () => ( { retModalType, setRetModalType } ),
        [ retModalType, setRetModalType ]
    );

    // Sources state - loaded from sbCustomizer (set by PHP in SBR_ReviewAlert_Builder)
    const [ sourcesList, setSourcesList ] = useState( sbCustomizer?.sourcesList || [] );
    const sources = useMemo(
        () => ( { sourcesList, setSourcesList } ),
        [ sourcesList, setSourcesList ]
    );

    const [ sourcesNumber, setSourcesNumber ] = useState( sbCustomizer?.sourcesCount || 0 );
    const sourcesCount = useMemo(
        () => ( { sourcesNumber, setSourcesNumber } ),
        [ sourcesNumber, setSourcesNumber ]
    );

    // Facebook modal state (required by CustomizerSourcesModal)
    const [ connectFacebookActive, setConnectFacebookActive ] = useState( sbCustomizer?.newSourceData !== undefined );
    const fbModal = useMemo(
        () => ( { connectFacebookActive, setConnectFacebookActive } ),
        [ connectFacebookActive, setConnectFacebookActive ]
    );

    // Allowed tiers for provider filtering (required by AddSourceModal inside CustomizerSourcesModal)
    const initialTiers = sbCustomizer?.pluginStatus?.tier_allowed_providers;
    const [ tiers, setTiers ] = useState(
        Array.isArray( initialTiers ) ? initialTiers : [ 'google', 'yelp' ]
    );
    const allowedTiers = useMemo(
        () => ( { tiers: Array.isArray( tiers ) ? tiers : [ 'google', 'yelp' ], setTiers } ),
        [ tiers, setTiers ]
    );

    // Bulk history modal state (required by AddSourceModal)
    const [ bulkHistoryModalNotice, setBulkHistoryModalNotice ] = useState( false );
    const bulkModal = useMemo(
        () => ( { bulkHistoryModalNotice, setBulkHistoryModalNotice } ),
        [ bulkHistoryModalNotice, setBulkHistoryModalNotice ]
    );

    /**
     * Handle create new popup - show source selection modal
     */
    const handleCreateNew = () => {
        // TODO: Re-enable upsell checks after debugging
        // Free user check - disabled for now
        // if ( !isPro && !sbCustomizer?.isPro ) {
        //     SbUtils.openUpsellModal( 'reviewAlertFeature', upsellModal );
        //     return;
        // }

        // TODO: Re-enable after debugging
        // Check popup limit for non-Pro Plus users
        // if ( popupsList.length >= 1 && !sbCustomizer?.isProPlus ) {
        //     SbUtils.openUpsellModal( 'reviewAlertMultiple', upsellModal );
        //     return;
        // }

        // Show source selection modal
        setShowSourcesModal( true );
    };

    /**
     * Handle sources selected from modal - create popup and redirect
     */
    const handleSourcesSelected = ( selectedSources ) => {
        if ( !selectedSources || selectedSources.length === 0 ) {
            setShowSourcesModal( false );
            return;
        }

        setShowSourcesModal( false );
        setIsCreatingPopup( true );

        // Get first source name for popup name
        const firstSource = sourcesList.find( s => s.account_id === selectedSources[0] );
        const popupName = firstSource?.name
            ? `${ firstSource.name } ${ __( 'Review Alert', 'reviews-feed' ) }`
            : __( 'New Review Alert', 'reviews-feed' );

        // Create settings with selected sources
        const defaultSettings = sbCustomizer?.reviewAlerts?.defaults || {};
        const settingsWithSources = {
            ...defaultSettings,
            sources: selectedSources
        };

        const formData = {
            action: 'sbr_review_alert_save',
            nonce: sbCustomizer.nonce,
            name: popupName,
            settings: JSON.stringify( settingsWithSources )
        };

        const notificationsContent = {
            success: {
                icon: 'success',
                text: __( 'Review alert created successfully', 'reviews-feed' )
            }
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                if ( data?.data?.popup?.id ) {
                    // Redirect to customizer with new popup ID
                    window.location.href = sbCustomizer.builderUrl + '&popup_id=' + data.data.popup.id;
                } else {
                    setIsCreatingPopup( false );
                }
            },
            editorTopLoader,
            editorNotification,
            notificationsContent
        );
    };

    return (
        <ReviewAlertsScreenContext.Provider
            value={ {
                sbCustomizer,
                popupsList,
                setPopupsList,
                popupsCount,
                setPopupsCount,
                setShowSourcesModal,
                editorTopLoader,
                editorNotification,
                editorConfirmDialog,
                isPro: isPro || sbCustomizer?.isPro,
                upsellModal,
                // Header context compatibility
                apiKeyModal,
                apis,
                apiLimits,
                freeRet,
                addSModal,
                freeRetModal,
                noticeBar,
                noticeBarMemo: { topNoticeBar: {} },
                editorFeedData: { feedData: [] },
                headerClasses: '',
                globalNotices: null,
                // Sources - required for CustomizerSourcesModal
                sources,
                sourcesCount,
                fbModal,
                // Required for AddSourceModal inside CustomizerSourcesModal
                allowedTiers,
                bulkModal
            } }
        >
            <Header
                className="sb-dashboard-header"
                heading={ __( 'Review Alerts', 'reviews-feed' ) }
                editorTopLoader={ editorTopLoader }
                topNoticeBar={ noticeBar?.topNoticeBar }
                setTopNoticeBar={ ( e ) => {
                    noticeBar?.setTopNoticeBar?.( e );
                } }
                showHelpButton={ true }
            />

            { isCreatingPopup && <FullScreenLoader /> }

            <section className="sb-full-wrapper sb-fs">
                {
                    sbCustomizer?.adminNoticeContent !== null &&
                    <section
                        className="sb-fs"
                        dangerouslySetInnerHTML={ { __html: sbCustomizer?.adminNoticeContent } }
                    />
                }

                <div className="sb-dashboard-heading sb-fs">
                    <h2 className="sb-h2">
                        { __( 'Review Alerts', 'reviews-feed' ) }
                    </h2>
                    <div className="sb-dashboard-action-btns">
                        <Button
                            customClass="sb-dashboard-btn"
                            type="primary"
                            size="small"
                            icon="plus"
                            iconPosition="left"
                            iconSize="11"
                            text={ __( 'Add New', 'reviews-feed' ) }
                            onClick={ handleCreateNew }
                        />
                    </div>
                </div>

                { popupsList.length === 0 && (
                    <ReviewAlertsEmptyState />
                ) }

                { popupsList.length > 0 && (
                    <ReviewAlertsList />
                ) }
            </section>

            { showSourcesModal && (
                <ModalContainer
                    size="medium"
                    closebutton={ true }
                    onClose={ () => setShowSourcesModal( false ) }
                >
                    <CustomizerSourcesModal
                        selectedSources={ [] }
                        fbModal={ fbModal }
                        useDbId={ true }
                        onCancel={ () => setShowSourcesModal( false ) }
                        onUpdateSources={ handleSourcesSelected }
                        title={ __( 'Select Sources', 'reviews-feed' ) }
                        description={ __( "We will display reviews in the popup from the sources you select below. Don't worry, you can always change this later.", 'reviews-feed' ) }
                        buttonText={ __( 'Create Review Alert', 'reviews-feed' ) }
                        buttonIcon="chevron-right"
                        buttonIconPosition="right"
                    />
                </ModalContainer>
            ) }
        </ReviewAlertsScreenContext.Provider>
    );
};

export default ReviewAlertsScreen;
